/**
 * Framework Applicability Engine
 *
 * Determines which compliance/regulatory frameworks apply to an organization
 * based on industry profile, organizational characteristics, and business
 * scoping selections. Computes assessment coverage per framework.
 */

import frameworkCatalog from '../data/frameworkCatalog.json'

const { frameworks, triggerMapping, domainNames, totalDomains } = frameworkCatalog

/**
 * Determine which frameworks apply to the organization and why.
 *
 * @param {string} profileKey - The threat profile vertical key (e.g., 'TP-HC')
 * @param {Array} profileFrameworks - Frameworks listed in the profile's regulatoryContext
 * @param {Array} orgCharLabels - Selected organizational characteristic labels
 * @param {Array} orgCharKeys - Selected organizational characteristic keys (e.g., ['publicly-traded', 'payment-card'])
 * @param {Array} frameworkOverlayKeys - Selected framework overlay keys from rf-1 (e.g., ['hipaa', 'pci-dss'])
 * @returns {Array} Applicable frameworks with metadata
 */
export function getApplicableFrameworks(
  profileKey,
  profileFrameworks = [],
  orgCharKeys = [],
  frameworkOverlayKeys = [],
  adminSelectedFrameworks = []
) {
  const applicable = new Map()

  // Helper to add a framework with a reason
  function addFramework(frameworkId, reason) {
    const fw = frameworks[frameworkId]
    if (!fw) return
    if (!applicable.has(frameworkId)) {
      applicable.set(frameworkId, { ...fw, reasons: [reason] })
    } else {
      const existing = applicable.get(frameworkId)
      if (!existing.reasons.includes(reason)) {
        existing.reasons.push(reason)
      }
    }
  }

  // 1. Match frameworks from trigger mapping based on profile
  for (const [trigger, mapping] of Object.entries(triggerMapping)) {
    if (mapping.matchProfiles.includes(profileKey)) {
      // Find which frameworks have this trigger
      for (const [fwId, fw] of Object.entries(frameworks)) {
        if (fw.triggers.includes(trigger)) {
          addFramework(fwId, 'Applicable based on the selected industry profile')
        }
      }
    }
  }

  // 2. Match from organizational characteristics
  for (const charKey of orgCharKeys) {
    for (const [trigger, mapping] of Object.entries(triggerMapping)) {
      if (mapping.matchOrgChars.includes(charKey)) {
        for (const [fwId, fw] of Object.entries(frameworks)) {
          if (fw.triggers.includes(trigger)) {
            addFramework(fwId, 'Applicable based on organizational characteristics')
          }
        }
      }
    }
  }

  // 3. Match from explicit framework overlay selections
  for (const overlayKey of frameworkOverlayKeys) {
    // Direct match — overlay key matches framework ID
    if (frameworks[overlayKey]) {
      addFramework(overlayKey, 'Selected in compliance requirements')
    }
    // Check trigger mapping for overlay-triggered frameworks
    for (const [trigger, mapping] of Object.entries(triggerMapping)) {
      if (mapping.matchFrameworkOverlays.includes(overlayKey)) {
        for (const [fwId, fw] of Object.entries(frameworks)) {
          if (fw.triggers.includes(trigger)) {
            addFramework(fwId, 'Selected in compliance requirements')
          }
        }
      }
    }
  }

  // 4. Match frameworks that appear in profileFrameworks by name
  for (const pfName of profileFrameworks) {
    const normalized = pfName.toLowerCase()
    for (const [fwId, fw] of Object.entries(frameworks)) {
      if (
        normalized.includes(fw.name.toLowerCase()) ||
        fw.name.toLowerCase().includes(normalized) ||
        (fw.fullName && normalized.includes(fw.fullName.toLowerCase()))
      ) {
        addFramework(fwId, 'Listed in industry regulatory context')
      }
    }
  }

  // 5. Add admin-selected frameworks
  for (const fwId of adminSelectedFrameworks) {
    if (frameworks[fwId]) {
      addFramework(fwId, 'Added by advisory team')
    }
  }

  // Filter: only include auto-detected frameworks if they are regulatory or industry category,
  // or if they were explicitly selected by admin or in compliance requirements.
  // Voluntary/framework-category items only show if admin-selected or from rf-1.
  const explicitSources = new Set([
    ...frameworkOverlayKeys,
    ...adminSelectedFrameworks
  ])

  // Convert to array, filter voluntary auto-includes, and compute coverage
  return Array.from(applicable.values())
    .filter(fw => {
      // Always keep regulatory and industry frameworks
      if (fw.category === 'regulatory' || fw.category === 'industry') return true
      // Keep voluntary frameworks only if explicitly selected
      return explicitSources.has(fw.id)
    })
    .map(fw => ({
      ...fw,
      coverage: computeCoverage(fw)
    }))
}

/**
 * Compute how much of a framework's scope is covered by the maturity assessment.
 *
 * @param {Object} framework - Framework object with assessmentDomains
 * @returns {Object} Coverage data
 */
function computeCoverage(framework) {
  const coveredDomains = framework.assessmentDomains || []
  const coveredCount = coveredDomains.length
  const percentage = Math.round((coveredCount / totalDomains) * 100)

  return {
    coveredDomains: coveredDomains.map(d => ({
      id: d,
      name: domainNames[String(d)] || `Domain ${d}`
    })),
    coveredCount,
    totalDomains,
    percentage,
    totalControlAreas: framework.totalControlAreas
  }
}

/**
 * Compute aggregate coverage across all applicable frameworks.
 *
 * @param {Array} applicableFrameworks - Output of getApplicableFrameworks
 * @returns {Object} Aggregate coverage stats
 */
export function getAggregateCoverage(applicableFrameworks) {
  if (!applicableFrameworks.length) {
    return { percentage: 0, frameworkCount: 0, avgCoverage: 0 }
  }

  // Collect all unique domains referenced across all applicable frameworks
  const allRequiredDomains = new Set()
  for (const fw of applicableFrameworks) {
    for (const d of fw.assessmentDomains || []) {
      allRequiredDomains.add(d)
    }
  }

  const coveredByAssessment = allRequiredDomains.size
  const percentage = Math.round((coveredByAssessment / totalDomains) * 100)

  const avgCoverage = Math.round(
    applicableFrameworks.reduce((sum, fw) => sum + (fw.coverage?.percentage || 0), 0) /
    applicableFrameworks.length
  )

  return {
    percentage,
    frameworkCount: applicableFrameworks.length,
    domainsReferenced: coveredByAssessment,
    totalDomains,
    avgCoverage
  }
}

export { domainNames, totalDomains }
